/**
 * Multiple Product Options JavaScript (updated for bundles and Horizon theme)
 * - Tracks selected variants (with priceRaw and optional signed priceDelta)
 * - Renders displayed total = main product price + sum(modifiers) + bundle items
 * - Supports bundles of products (multiple products with their variants)
 * - Integrates with the existing add-to-cart flow
 * - Auto-selects first option for mandatory sections
 * - Sends selected options as line item properties on main product and bundle items
 */

class MultipleProductOptions {
  constructor() {
    this.selectedVariants = new Map(); // key: sectionTitle, value: {id, price, priceRaw, priceDelta, element} OR array of same
    this.selectedBundles = new Map(); // key: bundleId, value: {id, variants, priceRaw, priceDelta}
    this.mandatorySections = new Set();
    this.init();
  }

  init() {
    this.setupAccordions();
    this.setupVariantSelection();
    this.initializeMandatorySections();     // mandatory sections first
    this.preselectDefaultVariant();         // then respect any preselected .selected in DOM
    this.autoSelectFirstForMandatory();     // then auto-select first in mandatory if still empty
    this.setupAddToCart();
    this.setupQuantityInput();
    this.setupVariantIdChangeListener(); 
    this.setupDropdowns();
  }

  /**
   * Setup accordion functionality for option sections
   */
  setupAccordions() {
    const accordions = document.querySelectorAll('.option-accordion');

    accordions.forEach((accordion, index) => {
      const title = accordion.querySelector('.option-title');
      const content = accordion.querySelector('.option-content');

      // Add chevron icon if not present
      if (title && !title.querySelector('.chevron')) {
        const chevron = document.createElement('span');
        chevron.className = 'chevron';
        chevron.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
            <path d="M26.7081 12.7076L16.7081 22.7076C16.6152 22.8005 16.5049 22.8743 16.3835 22.9246C16.2621 22.975 16.132 23.0009 16.0006 23.0009C15.8691 23.0009 15.739 22.975 15.6176 22.9246C15.4962 22.8743 15.3859 22.8005 15.2931 22.7076L5.29306 12.7076C5.10542 12.5199 5 12.2654 5 12.0001C5 11.7347 5.10542 11.4802 5.29306 11.2926C5.4807 11.1049 5.73519 10.9995 6.00056 10.9995C6.26592 10.9995 6.52042 11.1049 6.70806 11.2926L16.0006 20.5863L25.2931 11.2926C25.386 11.1997 25.4963 11.126 25.6177 11.0757C25.7391 11.0254 25.8692 10.9995 26.0006 10.9995C26.132 10.9995 26.2621 11.0254 26.3835 11.0757C26.5048 11.126 26.6151 11.1997 26.7081 11.2926C26.801 11.3855 26.8747 11.4958 26.9249 11.6172C26.9752 11.7386 27.0011 11.8687 27.0011 12.0001C27.0011 12.1315 26.9752 12.2616 26.9249 12.383C26.8747 12.5044 26.801 12.6147 26.7081 12.7076Z" fill="black"/>
          </svg>
        `;
        title.appendChild(chevron);
      }

      // Add click handler
      if (title) {
        title.addEventListener('click', () => {
          this.toggleAccordion(accordion);
        });
      }

      this.openAccordion(accordion);
    });
  }

  toggleAccordion(accordion) {
    const isActive = accordion.classList.contains('active');
    if (isActive) {
      this.closeAccordion(accordion);
    } else {
      this.openAccordion(accordion);
    }
  }

  openAccordion(accordion) {
    accordion.classList.add('active');
    const content = accordion.querySelector('.option-content');
    if (content) content.style.display = 'block';
  }

  closeAccordion(accordion) {
    accordion.classList.remove('active');
    const content = accordion.querySelector('.option-content');
    if (content) content.style.display = 'none';
  }

  /**
   * Setup variant selection functionality
   */
  setupVariantSelection() {
    const variantItems = document.querySelectorAll('.product-variant-item');

    variantItems.forEach(item => {
      item.addEventListener('click', () => {
        this.selectVariant(item);
      });

      // Add keyboard support
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.selectVariant(item);
        }
      });

      // Make focusable
      if (!item.hasAttribute('tabindex')) item.setAttribute('tabindex', '0');
    });
  }

  /**
   * Preselect any default .selected items present in DOM (supports multiple preselected per section)
   */
  preselectDefaultVariant() {
    const preselected = document.querySelectorAll('.product-variant-item.selected');
    if (preselected && preselected.length) {
      preselected.forEach(item => {
        const productListContent = item.closest('.product-list-content');
        const isMulti = productListContent && productListContent.dataset && productListContent.dataset.multiple === 'true';
        const sectionTitle = this.getSectionTitle(item);
        const variantId = item.dataset.id;

        const priceRaw = parseInt(item.dataset.priceRaw || item.dataset.priceraw || 0, 10) || 0;
        const priceDeltaRaw = (typeof item.dataset.priceDelta !== 'undefined' && item.dataset.priceDelta !== '') ? parseInt(item.dataset.priceDelta, 10) : null;

        if (isMulti) {
          if (!this.selectedVariants.has(sectionTitle)) {
            this.selectedVariants.set(sectionTitle, []);
          }
          const selectedList = this.selectedVariants.get(sectionTitle);
          if (!selectedList.find(v => v.id === variantId)) {
            selectedList.push({
              id: variantId,
              price: item.dataset.price || '',
              priceRaw: priceRaw,
              priceDelta: priceDeltaRaw,
              element: item
            });
          }
        } else {
          this.selectedVariants.set(sectionTitle, {
            id: variantId,
            price: item.dataset.price || '',
            priceRaw: priceRaw,
            priceDelta: priceDeltaRaw,
            element: item
          });
        }

        // ensure hidden input matches DOM preselected state
        const hiddenInput = productListContent ? productListContent.querySelector('input[name="variant-id"]') : null;
        if (hiddenInput) {
          if (isMulti) {
            const ids = this.selectedVariants.get(sectionTitle).map(v => v.id);
            hiddenInput.value = ids.join(',');
          } else {
            hiddenInput.value = variantId;
          }
        }
      });

      // run validation and price render after preselects
      this.updateValidation();
      this.updateDisplayedPrice();
    }
  }

  /**
   * Select a variant item (supports multi-select for sections with data-multiple="true")
   */
  selectVariant(item) {
    const productListContent = item.closest('.product-list-content');
    const variantId = item.dataset.id;
    const sectionTitle = this.getSectionTitle(item);
    const isMulti = productListContent && productListContent.dataset && productListContent.dataset.multiple === 'true';

    const priceRaw = parseInt(item.dataset.priceRaw || item.dataset.priceraw || 0, 10) || 0;
    const priceDeltaRaw = (typeof item.dataset.priceDelta !== 'undefined' && item.dataset.priceDelta !== '') ? parseInt(item.dataset.priceDelta, 10) : null;

    if (isMulti) {
      // Multi-select: toggle item on/off
      const isNowSelected = item.classList.toggle('selected');

      if (!this.selectedVariants.has(sectionTitle)) {
        this.selectedVariants.set(sectionTitle, []);
      }
      const selectedList = this.selectedVariants.get(sectionTitle);

      if (isNowSelected) {
        if (!selectedList.find(v => v.id === variantId)) {
          selectedList.push({
            id: variantId,
            price: item.dataset.price || '',
            priceRaw: priceRaw,
            priceDelta: priceDeltaRaw,
            element: item
          });
        }
      } else {
        const filtered = selectedList.filter(v => v.id !== variantId);
        if (filtered.length > 0) {
          this.selectedVariants.set(sectionTitle, filtered);
        } else {
          this.selectedVariants.delete(sectionTitle);
        }
      }

      // Update hidden input (comma-separated)
      const hiddenInput = productListContent ? productListContent.querySelector('input[name="variant-id"]') : null;
      if (hiddenInput) {
        const ids = this.selectedVariants.has(sectionTitle) ? this.selectedVariants.get(sectionTitle).map(v => v.id) : [];
        hiddenInput.value = ids.join(',');
      }
    } else {
      // Single-select: DO NOT allow deselect on clicking the same item again
      const previousSelected = productListContent.querySelector('.product-variant-item.selected');

      if (previousSelected === item) {
        // Stop unselect from happening (prevents double-click unselect)
        return;
      }

      // Normal single-select behaviour: switch selection
      if (previousSelected) previousSelected.classList.remove('selected');
      item.classList.add('selected');

      const hiddenInput = productListContent ? productListContent.querySelector('input[name="variant-id"]') : null;
      if (hiddenInput) hiddenInput.value = variantId;

      this.selectedVariants.set(sectionTitle, {
        id: variantId,
        price: item.dataset.price || '',
        priceRaw: priceRaw,
        priceDelta: priceDeltaRaw,
        element: item
      });
    }

    // Update validation and displayed price
    this.updateValidation();
    this.updateDisplayedPrice();
  }

  /**
   * Get section title for a variant item
   */
  getSectionTitle(item) {
    const productListContent = item.closest('.product-list-content');
    const titleElement = productListContent ? productListContent.querySelector('.product-list-title') : null;
    return titleElement ? titleElement.textContent.trim() : '';
  }

  /**
   * Setup dropdown functionality for variant selection
   */
  setupDropdowns() {
    const dropdowns = document.querySelectorAll('.product-variant-dropdown');

    dropdowns.forEach(dropdown => {
      dropdown.addEventListener('change', (e) => {
        this.selectVariantFromDropdown(e.target);
      });
    });
  }

  selectVariantFromDropdown(dropdown) {
    const variantId = dropdown.value;
    const sectionTitle = this.getSectionTitle(dropdown);

    // ✅ grab the selected option (not the whole select)
    const selectedOption = dropdown.options[dropdown.selectedIndex];
    if (!selectedOption) return;

    const priceRaw = parseInt(selectedOption.dataset.priceRaw || selectedOption.dataset.priceraw || 0, 10) || 0;
    const priceDeltaRaw =
        (typeof selectedOption.dataset.priceDelta !== 'undefined' && selectedOption.dataset.priceDelta !== '')
        ? parseInt(selectedOption.dataset.priceDelta, 10)
        : null;

    // ✅ store the OPTION as the element so label/price are correct
    this.selectedVariants.set(sectionTitle, {
        id: variantId,
        price: selectedOption.dataset.price || '',
        priceRaw: priceRaw,
        priceDelta: priceDeltaRaw,
        element: selectedOption,
        dropdownEl: dropdown // optional (only if you want to keep reference)
    });

    const hiddenInput = dropdown.closest('.product-list-content')?.querySelector('input[name="variant-id"]');
    if (hiddenInput) hiddenInput.value = variantId;

    this.updateValidation();
    this.updateDisplayedPrice();
    }



  /**
   * Initialize mandatory sections tracking
   */
  initializeMandatorySections() {
    const mandatoryAccordions = document.querySelectorAll('.option-accordion[data-mandatory="true"]');

    mandatoryAccordions.forEach(accordion => {
      const productListContents = accordion.querySelectorAll('.product-list-content');

      productListContents.forEach(content => {
        const titleElement = content.querySelector('.product-list-title');
        if (titleElement && titleElement.querySelector('span')) {
          const title = titleElement.textContent.trim();
          this.mandatorySections.add(title);
        }
      });
    });
  }

  /**
   * Auto-select the first option for each mandatory section
   * if nothing is currently selected in that section.
   */
  autoSelectFirstForMandatory() {
    const mandatoryAccordions = document.querySelectorAll('.option-accordion[data-mandatory="true"]');

    mandatoryAccordions.forEach(accordion => {
      const productListContents = accordion.querySelectorAll('.product-list-content');

      productListContents.forEach(content => {
        const alreadySelected = content.querySelector('.product-variant-item.selected');
        if (alreadySelected) return;

        const firstOption = content.querySelector('.product-variant-item');
        if (firstOption) {
          this.selectVariant(firstOption);
        }
      });
    });
  }

  getCurrency() {
    return (
      document.querySelector('[data-currency]')?.dataset.currency ||
      document.querySelector('.current-price')?.dataset.currency ||
      window.Shopify?.currency?.active ||
      'GBP'
    );
  }


  /**
   * Helper to get a human-readable label for a variant item
   * Used for line item properties (e.g. "Size: Large")
   */
    getVariantLabel(element) {
    if (!element) return '';

    // ✅ If select is passed, return only selected option text
    if (element.tagName === 'SELECT') {
        const opt = element.options[element.selectedIndex];
        return (opt?.textContent || '').trim();
    }

    // ✅ If option is passed, return its text only
    if (element.tagName === 'OPTION') {
        return (element.textContent || '').trim();
    }

    if (element.dataset && element.dataset.label) return element.dataset.label.trim();

    const labelCandidate =
        element.querySelector?.('.product-variant-name') ||
        element.querySelector?.('.product-variant-label') ||
        element;

    return (labelCandidate.textContent || '').trim();
    }


  /**
   * Get the main product variant id
   * Priority:
   *  1) .main-product-input (hidden input)
   *  2) product form input[name="id"]
   *  3) data-id on .custom-option-button
   */
  getMainProductId() {
    const explicit = document.querySelector('input.main-product-input');
    if (explicit && explicit.value) return explicit.value;

    const productForm = document.querySelector('form[action^="/cart/add"]');
    if (productForm) {
      const idInput = productForm.querySelector('input[name="id"]');
      if (idInput && idInput.value) return idInput.value;
    }

    const btn = document.querySelector('.custom-option-button');
    if (btn && btn.dataset.id) return btn.dataset.id;

    return null;
  }

  getProductForm() {
    return (
      document.querySelector('form[data-type="add-to-cart-form"]') ||
      document.querySelector('form[action^="/cart/add"]')
    );
  }

  getLineItemPropertyKey(name) {
    const match = /^properties\[(.+)\]$/.exec(name || '');
    return match ? match[1] : null;
  }

  isAddonSelectionInput(input) {
    return Boolean(
      input &&
      input.classList &&
      (
        input.classList.contains('pdp-addon-card__input') ||
        input.classList.contains('addon-option__input')
      )
    );
  }

  getAddonPriceCents(element) {
    const addonRoot = element && element.closest ? element.closest('[data-addon-price]') : null;
    return addonRoot ? parseInt(addonRoot.dataset.addonPrice || 0, 10) || 0 : 0;
  }

  collectFormLineItemProperties() {
    const properties = {};
    const productForm = this.getProductForm();
    const formId = productForm && productForm.id ? productForm.id : '';
    const propertyInputs = [];
    const seenInputs = new Set();

    const registerInput = (input) => {
      if (!input || seenInputs.has(input)) return;
      if (this.isAddonSelectionInput(input)) return;

      const key = this.getLineItemPropertyKey(input.name);
      if (!key || key.charAt(0) === '_') return;

      seenInputs.add(input);
      propertyInputs.push(input);
    };

    if (productForm && productForm.elements) {
      Array.from(productForm.elements).forEach(registerInput);
    }

    document
      .querySelectorAll('input[name^="properties["], textarea[name^="properties["], select[name^="properties["]')
      .forEach((input) => {
        const isInsideForm = productForm ? productForm.contains(input) : false;
        const isAssociatedWithForm = Boolean(formId) && input.getAttribute('form') === formId;

        if (!productForm || isInsideForm || isAssociatedWithForm) {
          registerInput(input);
        }
      });

    propertyInputs.forEach((input) => {
      if (input.disabled) return;

      const key = this.getLineItemPropertyKey(input.name);
      if (!key) return;

      let value = '';

      if (input instanceof HTMLInputElement) {
        if ((input.type === 'checkbox' || input.type === 'radio') && !input.checked) return;
        value = input.value;
      } else if (input instanceof HTMLTextAreaElement) {
        value = input.value;
      } else if (input instanceof HTMLSelectElement) {
        if (input.multiple) {
          value = Array.from(input.selectedOptions)
            .map((option) => option.value)
            .filter(Boolean)
            .join(', ');
        } else {
          value = input.value;
        }
      }

      if (typeof value !== 'string') {
        value = value == null ? '' : String(value);
      }

      value = value.trim();
      if (!value) return;

      if (properties[key] && properties[key] !== value) {
        properties[key] = `${properties[key]}, ${value}`;
      } else {
        properties[key] = value;
      }
    });

    return properties;
  }

  collectFreeAddonProperties() {
    const properties = {};

    document.querySelectorAll('[data-addon-price]').forEach((addon) => {
      const addonPrice = parseInt(addon.dataset.addonPrice || 0, 10) || 0;
      const checkbox = addon.querySelector('.pdp-addon-card__input, .addon-option__input');
      const isActive =
        (checkbox && checkbox.checked) ||
        addon.dataset.addonActive === 'true' ||
        addon.classList.contains('is-active');

      if (!isActive || addonPrice > 0 || !checkbox || !checkbox.name) return;

      const key = this.getLineItemPropertyKey(checkbox.name);
      const value = (checkbox.value || '').trim();

      if (!key || !value) return;
      properties[key] = value;
    });

    return properties;
  }

  getSelectedAddonItems(quantity) {
    const items = [];
    const seenVariantIds = new Set();

    document.querySelectorAll('[data-addon-variant-id]').forEach((addon) => {
      const variantId = addon.dataset.addonVariantId;
      const addonPrice = parseInt(addon.dataset.addonPrice || 0, 10) || 0;
      const checkbox = addon.querySelector('.pdp-addon-card__input, .addon-option__input');
      const isActive =
        (checkbox && checkbox.checked) ||
        addon.dataset.addonActive === 'true' ||
        addon.classList.contains('is-active');

      if (!isActive || addonPrice <= 0 || !variantId || seenVariantIds.has(String(variantId))) return;

      seenVariantIds.add(String(variantId));
      items.push({
        id: variantId,
        quantity: quantity,
        source: 'addon'
      });
    });

    return items;
  }

  /**
   * ✅ NEW: Read Fabric Color selection directly from your picker UI
   */
  getFabricColorFromPicker() {
    const checked = document.querySelector('[data-fabric-color-picker] input.custom-color-option:checked');
    if (!checked) return null;

    return {
      color: checked.getAttribute('data-color-name') || checked.value || '',
      image: checked.getAttribute('data-color-image') || ''
    };
  }

  /**
   * Build line item properties for the MAIN product
   * e.g. { _main_id: 123, _main_id_timestamp: 11111111, "Size": "Large", "Color": "Blue" }
   */
  buildMainLineItemProperties(mainId, timestamp) {
    const properties = {
      _main_id: mainId,
      _main_id_timestamp: timestamp
    };

    // Add Fabric Color to line item properties (main product)
    const fabric = this.getFabricColorFromPicker();
    if (fabric && fabric.color) {
      properties['Fabric Color'] = fabric.color;
      if (fabric.image) properties['_Fabric Color Image'] = fabric.image;
    }

    this.selectedVariants.forEach((value, sectionTitle) => {
      if (!sectionTitle) return;

      const labels = [];

      if (Array.isArray(value)) {
        value.forEach(v => {
          const label = this.getVariantLabel(v.element);
          if (label) labels.push(label);
        });
      } else if (value && value.element) {
        const label = this.getVariantLabel(value.element);
        if (label) labels.push(label);
      }

      if (labels.length) {
        const currency = this.getCurrency();
        const pricedLabels = [];

        if (Array.isArray(value)) {
          value.forEach(v => {
            const label = this.getVariantLabel(v.element);
            if (!label) return;

            const cents =
              v.priceDelta !== null && v.priceDelta !== undefined
                ? v.priceDelta
                : v.priceRaw || 0;

            const priceText =
              cents === 0
                ? 'Included'
                : `${cents > 0 ? '+' : '-'}${this.formatMoney(Math.abs(cents), currency)}`;

            pricedLabels.push(`${label} (${priceText})`);
          });
        } else {
          const label = this.getVariantLabel(value.element);
          if (label) {
            const cents =
              value.priceDelta !== null && value.priceDelta !== undefined
                ? value.priceDelta
                : value.priceRaw || 0;

            const priceText =
              cents === 0
                ? 'Included'
                : `${cents > 0 ? '+' : '-'}${this.formatMoney(Math.abs(cents), currency)}`;

            pricedLabels.push(`${label} (${priceText})`);
          }
        }

        if (pricedLabels.length) {
          properties[sectionTitle] = pricedLabels.join(', ');
        }
      }

    });

    // ✅ NEW: Include custom instructions field value
    const instructionsField = document.querySelector('#line-item-instructions');
    if (instructionsField && instructionsField.value) {
      properties['Instructions'] = instructionsField.value;
    }

    return {
      ...properties,
      ...this.collectFreeAddonProperties(),
      ...this.collectFormLineItemProperties()
    };
  }


  /**
   * Update validation state
   */
  updateValidation() {
    const allMandatorySelected = Array.from(this.mandatorySections).every(section =>
      this.selectedVariants.has(section)
    );

    const addToCartBtn = document.querySelector('.custom-option-button');
    if (addToCartBtn) {
      if (allMandatorySelected) {
        addToCartBtn.classList.remove('mandatory-error-state');
      } else {
        addToCartBtn.classList.add('mandatory-error-state');
      }
    }

    this.hideError();
  }

  showError(message = 'ALL MANDATORY ITEMS MUST BE SELECTED') {
    let errorElement = document.querySelector('.mandatory-error');

    if (!errorElement) {
      errorElement = this.createErrorElement(message);
      const container = document.querySelector('.custom-options-container');
      container.insertBefore(errorElement, container.firstChild);
    }

    errorElement.classList.add('show');

    // Scroll to error
    errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  hideError() {
    const errorElement = document.querySelector('.mandatory-error');
    if (errorElement) {
      errorElement.classList.remove('show');
    }
  }

  createErrorElement(message) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'mandatory-error';

    const icon = document.createElement('div');
    icon.className = 'mandatory-error-icon';
    icon.textContent = '×';

    const text = document.createElement('span');
    text.textContent = message;

    errorDiv.appendChild(icon);
    errorDiv.appendChild(text);

    return errorDiv;
  }

  setupAddToCart() {
    const addToCartBtn = document.querySelector('.custom-option-button');

    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleAddToCart();
      });
    }
  }

  async handleAddToCart() {
    // Validate mandatory selections
    if (!this.validateMandatorySelections()) {
      return;
    }

    const addToCartBtn = document.querySelector('.custom-option-button');
    const originalText = addToCartBtn ? addToCartBtn.textContent : '';

    // Get main product id using robust helper
    const mainId = this.getMainProductId();

    // Show loading state
    if (addToCartBtn) {
      addToCartBtn.classList.add('loading');
      addToCartBtn.disabled = true;
    }

    try {
      // Get quantity
      const quantity = this.getQuantity();

      // Prepare cart items
      const cartItems = this.prepareCartItems(quantity);

      // Add items to cart
      await this.addItemsToCart(cartItems, mainId);

      // Open cart drawer
      this.openCartDrawer();

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showError('Error adding items to cart. Please try again.');
    } finally {
      // Reset button state
      if (addToCartBtn) {
        addToCartBtn.classList.remove('loading');
        addToCartBtn.disabled = false;
        addToCartBtn.textContent = originalText;
      }
    }
  }

  validateMandatorySelections() {
    const missingSelections = [];

    this.mandatorySections.forEach(section => {
      if (!this.selectedVariants.has(section)) {
        missingSelections.push(section);
      }
    });

    if (missingSelections.length > 0) {
      this.showError();

      // Scroll to first missing section
      const firstMissing = missingSelections[0];
      const accordion = this.findAccordionBySection(firstMissing);
      if (accordion) {
        this.openAccordion(accordion);
        accordion.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return false;
    }

    return true;
  }

  findAccordionBySection(sectionTitle) {
    const accordions = document.querySelectorAll('.option-accordion');

    for (const accordion of accordions) {
      const productListContents = accordion.querySelectorAll('.product-list-content');

      for (const content of productListContents) {
        const titleElement = content.querySelector('.product-list-title');
        if (titleElement && titleElement.textContent.trim() === sectionTitle) {
          return accordion;
        }
      }
    }

    return null;
  }

  getQuantity() {
    const quantityInput = document.querySelector('input.quantity__input');
    return quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;
  }

  prepareCartItems(quantity) {
    const items = [];

    // Add main product (always try via helper)
    const mainId = this.getMainProductId();
    if (mainId) {
      items.push({
        id: mainId,
        quantity: quantity
      });
    }

    // Add selected variants
    this.selectedVariants.forEach((variant) => {
      if (Array.isArray(variant)) {
        variant.forEach(v => {
          items.push({
            id: v.id,
            quantity: quantity
          });
        });
      } else if (variant && variant.id) {
        items.push({
          id: variant.id,
          quantity: quantity
        });
      }
    });

    // Add bundle items
    this.selectedBundles.forEach((bundle) => {
      bundle.variants.forEach(variant => {
        items.push({
          id: variant.id,
          quantity: quantity
        });
      });
    });

    this.getSelectedAddonItems(quantity).forEach((item) => {
      items.push(item);
    });

    return items;
  }

  async addItemsToCart(items, mainId) {
    // Generate timestamp in seconds to differentiate between different additions
    const timestamp = Math.floor(Date.now() / 1000);

    // Base properties for ALL items (used to group bundles)
    const baseProperties = {
      _main_id: mainId,
      _main_id_timestamp: timestamp
    };

    // Properties only for the MAIN product (includes selected options + Fabric Color)
    const mainItemProperties = this.buildMainLineItemProperties(mainId, timestamp);

    // Prepare all items for single request
    const formData = {
      items: items.map(item => {
        const isMain = String(item.id) === String(mainId);
        const properties =
          item.source === 'addon'
            ? {}
            : Object.assign(
                {},
                baseProperties,
                isMain ? mainItemProperties : {}
              );

        return {
          id: item.id,
          quantity: item.quantity,
          properties
        };
      })
    };

    // Get cart drawer element for sections to render
    const cartDrawer = document.querySelector('cart-drawer');
    let sectionsToRender = [];
    try {
      sectionsToRender = cartDrawer
        ? (typeof cartDrawer.getSectionsToRender === 'function'
            ? cartDrawer.getSectionsToRender().map(section => section.id)
            : [])
        : [];
    } catch (e) {
      sectionsToRender = [];
    }

    // Add sections to form data for cart drawer update
    if (sectionsToRender.length > 0) {
      formData.sections = sectionsToRender;
      formData.sections_url = window.location.pathname;
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(formData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to add items to cart');
    }

    const result = await response.json();

    // Update cart drawer if it exists
    if (cartDrawer && result.sections) {
      if (typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(result);
      } else {
        // fallback: set innerHTML where possible
        try {
          cartDrawer.innerHTML = result.sections_html || cartDrawer.innerHTML;
        } catch (e) {
          // noop
        }
      }
    }

    // Ensure cart drawer is not in empty state after adding items
    if (cartDrawer) {
      cartDrawer.classList.remove('is-empty');
      const cartDrawerItems = cartDrawer.querySelector('cart-drawer-items');
      if (cartDrawerItems) {
        cartDrawerItems.classList.remove('is-empty');
      }
    }

    return result;
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer) {
      cartDrawer.classList.remove('is-empty');
      const cartDrawerItems = cartDrawer.querySelector('cart-drawer-items');
      if (cartDrawerItems) {
        cartDrawerItems.classList.remove('is-empty');
      }

      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else {
        cartDrawer.classList.add('open');
      }
    } else {
      window.location.href = '/cart';
    }
  }

  setupQuantityInput() {
    const quantityInput = document.querySelector('input.quantity__input');
    if (quantityInput) {
      quantityInput.addEventListener('change', () => {
        this.updateValidation();
        this.updateDisplayedPrice();
      });
    }
  }

  /**
   * Format cents to currency string using Intl.NumberFormat
   * cents - integer (shop money integer, e.g. 1999 for 19.99)
   */
  formatMoney(cents, currency) {
    try {
      const value = (parseInt(cents, 10) || 0) / 100;
      const locale = navigator.language || 'en-US';
      return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(value);
    } catch (e) {
      return ((parseInt(cents, 10) || 0) / 100).toFixed(2) + ' ' + (currency || '');
    }
  }

setupVariantIdChangeListener() {
  const variantIdInput = document.querySelector('#variant-id');  // Specific input element with id="variant-id"

  if (variantIdInput) {
    // Listen for the 'input' event, which fires every time the value of the input field changes
    variantIdInput.addEventListener('input', () => {
      // Log the value of the variant-id input to the console whenever it changes
      console.log('Variant ID changed to:', variantIdInput.value);
      
      // Optionally, you can call other functions to update the displayed price or perform additional tasks
      this.updateDisplayedPrice();
    });
  } else {
    // If the element is not found, log a message to confirm the input field is missing
    console.log('Variant ID input not found.');
  }
}


  /**
   * Compute and render the total price = main product + sum of selected variant modifiers + bundle items
   */
  updateDisplayedPrice() {
  const priceEls = document.querySelectorAll('.current-price');  // Elements to update the price
  if (!priceEls || !priceEls.length) return;

  priceEls.forEach(priceEl => {
    const currency = priceEl.dataset.currency || 'GBP';  // Default currency, can be overwritten in data

    // Read the main product price (in cents)
    const mainRaw = parseInt(
      priceEl.dataset.priceProduct || 
      priceEl.dataset.mainPrice || 
      priceEl.dataset.defaultPrice || 
      0, 
      10
    ) || 0;

    // Initialize total price (main product + modifiers + bundles)
    let totalPrice = mainRaw;

    // Sum selected modifiers (e.g., size, color, etc.)
    let modifiersTotal = 0;

    // Iterate over selected variants and calculate the modifiers
    this.selectedVariants.forEach(value => {
      if (Array.isArray(value)) {
        value.forEach(v => {
          const el = v.element;
          if (!el) return;

          // Read price delta or raw price from the element's data attributes
          const deltaAttr = el.dataset.priceDelta || null;
          if (deltaAttr !== null) {
            const delta = parseInt(deltaAttr, 10);
            modifiersTotal += isNaN(delta) ? 0 : delta;
          } else {
            const rawPrice = parseInt(v.priceRaw || el.dataset.priceRaw || el.dataset.priceraw || 0, 10);
            modifiersTotal += isNaN(rawPrice) ? 0 : rawPrice;
          }
        });
      } else if (value && value.id) {
        const el = value.element;
        if (!el) return;

        // Read price delta or raw price for single variant
        const deltaAttr = el.dataset.priceDelta || null;
        if (deltaAttr !== null) {
          const delta = parseInt(deltaAttr, 10);
          modifiersTotal += isNaN(delta) ? 0 : delta;
        } else {
          const rawPrice = parseInt(value.priceRaw || el.dataset.priceRaw || el.dataset.priceraw || 0, 10);
          modifiersTotal += isNaN(rawPrice) ? 0 : rawPrice;
        }
      }
    });

    // Add the total modifiers to the base price
    totalPrice += modifiersTotal;

    // Add selected bundles price if applicable
    this.selectedBundles.forEach(bundle => {
      bundle.variants.forEach(variant => {
        totalPrice += parseInt(variant.priceRaw || 0, 10);
      });
    });

    // Update the price element with the formatted price
    priceEl.textContent = this.formatMoney(totalPrice, currency);
  });
}


  removeAllSelectedVariants() {
    const allItems = document.querySelectorAll('.product-variant-item.selected');
    allItems.forEach(i => i.classList.remove('selected'));

    this.selectedVariants.clear();
    this.selectedBundles.clear();
    this.updateDisplayedPrice();
    this.updateValidation();

    // After reset, auto-select first options for mandatory sections again
    this.autoSelectFirstForMandatory();
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('.custom-options-container')) {
    window.MultipleProductOptions = new MultipleProductOptions();
  }
});

// Export for potential external use (keeps previous behavior)
window.MultipleProductOptionsClass = MultipleProductOptions;

// Listen for Shopify main variant changes
document.addEventListener('variant:change', (event) => {
  // Try to update main-product-input when theme fires variant:change
  const variant = event && event.detail && event.detail.variant ? event.detail.variant : null;
  const mainInput = document.querySelector('input.main-product-input');
  if (variant && mainInput) {
    mainInput.value = variant.id;
  }

  if (window.MultipleProductOptions) {
    window.MultipleProductOptions.removeAllSelectedVariants();
  }
});
